import { useCallback, useEffect, useRef, useState } from "react";
import logo from "icons/smallLogo.svg";
import {
  changePaymentScheduleName,
  clearSlice,
  formatCurrency,
  getBrowserTimezoneName,
  getBrowserTimezoneOffset,
  getCardType,
  getCountDownAgreement,
  getGeneralDetailMarkers,
  isExpired,
} from "utils/HelperFunctions";
import {
  OfferData,
  OrderData,
  ScheduledItem,
  SignAgreementValues,
  AgreementPayload,
  GooglePayData,
  HandleStoreParams,
  PaymentAgreementResponse,
  LegacyAgreementResolverResponse,
} from "types/agreement";
import {
  addNotification,
  updateTokenExpiredStatus,
  updateTokenStatus,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import ScannerImg from "../../icons/bi_qr-code.png";
import Skeleton from "react-loading-skeleton";
import { OfferIcon } from "icons/payments/OfferIcon";
import InfoIcon from "../../icons/dashboard/InfoIcon";
import ActionPaymentMethod from "components/actionCenter/ActionPaymentMethod";
import { signatureForAgreement } from "store/services/agreementSignatureCache";
import Countdown from "components/ui-component/Countdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import {
  getPaymentAgreementData,
  getLegacyAgreementSlug,
  paymentDownload,
  signAgreement,
  setIsPaymentProcessing,
  updateLogoAndFavicon,
  updatePaymentPermission,
  clearAgreementSignature,
  companyMarkersGet,
} from "store/slices/paymentsSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import PaymentAgreementExpired from "components/actionCenter/PaymentAgreementExpired";
import PaymentAgreementSuccess from "components/actionCenter/PaymentAgreementSuccess";
import Footer from "components/layout/footer";
import CustomTooltip from "components/ui-component/CustomTooltip";
import CustomModal from "components/ui-component/CustomModal";
import { formatPaymentListDate } from "utils/DateFormat";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import { decode } from "utils/HelperFunctions";
import moment from "moment";
import { SettingsValue } from "types/payments";
import { RootState } from "store/store";
import { useNavigationGuard } from "hooks/useNavigationGuard";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { useIsLogin } from "store/slices/commonSlice";
import Button from "components/form/Button";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";
import {
  buildCanonicalAgreementRedirectUrl,
  getCanonicalAgreementSlug,
  getSameOriginAgreementRoute,
  isCanonicalAgreementSlug,
  isLegacyAgreementSlug,
} from "utils/agreementRoute";
import { isInvalidPublicLinkResult } from "utils/publicLinkValidation";
import { isAllowedClusterId } from "utils/clusterAccess";
import { buildAgreementReceiptEmailPayload } from "utils/agreementReceiptEmail";
import { SIGNING_FAILED_MSG } from "store/services/agreementSigning";

const AgreementContent = () => {
  const testAttrs = useTestAttrsFactory();
  const userData = useUserData();
  const portalSettings = usePortalSettingsData();
  const isLogin = useIsLogin();
  const navigate = useNavigate();
  const skipNextAgreementFetchRef = useRef<boolean>(false);
  const documentContainerRef = useRef<HTMLDivElement>(null);
  const sentReceiptInvoiceIdsRef = useRef<Set<string>>(new Set());

  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const drawerRef = useRef<HTMLSpanElement>(null);
  const { id: actionId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const [paymentLogo, setPaymentLogo] = useState(logo);
  const { payPalOrder, conFee, isPaymentProcessing } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [editVal, setEditVal] = useState<string>("0.00");
  const [isOffer, setIsOffer] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [timeDifff, setTimeDifff] = useState<number | null>(null);
  const [isAgreementSent, setIsAgreementSent] = useState<boolean>(false);
  const [isAgreementSuccess, setIsAgreementSuccess] = useState<boolean>(false);
  const [requestStatus, setRequestStatus] = useState<string>("Pending");
  const [agreementSentFailed, setAgreementSentFailed] = useState<string | null>(
    null
  );
  const [isGooglePayLoading, setIsGooglePayLoading] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isReplacedPopup, setIsReplacedPopup] = useState<boolean>(false);

  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const [expandedDate, setExpandedDate] = useState<string | undefined>();
  const [OfferData, setOfferData] = useState<OfferData>({
    offer_description: "hfrh fcnhrf nhfcrvnh vncf hjhj jhhj",
    offer_headline: "Get 10% off",
  });
  const [orderData, setOrderData] = useState<OrderData>({
    payment_schedule: "Daily",
    total_payments: "5",
    offer_amount: 550,
    grandTotal: 800,
    creation_date: "20/10/2024",
  });
  const [date, setDate] = useState<string | undefined>();
  const [oldAgreement, setOldAgreement] = useState<{
    id: number;
    status: string;
    replaced_by: string;
  } | null>(null);

  const [scheduled, setScheduled] = useState<ScheduledItem[]>([]);

  const sendEmail = async ({
    invoice_id,
    emails,
  }: {
    invoice_id?: string | number | null;
    emails?: string | string[] | null;
  }): Promise<void> => {
    const billingDate = scheduled?.[0]?.billing_date;
    const isRealTimePayment = Boolean(
      billingDate &&
      moment(billingDate).isValid() &&
      moment(billingDate).isSame(moment(), "day")
    );
    const receiptPayload = buildAgreementReceiptEmailPayload({
      isPaymentSuccessful: true,
      isRealTimePayment,
      invoiceId: invoice_id,
      accountId: userData?.data?.accountData?.accountid,
      emails: emails || userData?.data?.accountData?.email,
    });

    if (!receiptPayload) {
      return;
    }

    const receiptKey = String(receiptPayload.invoice_id);
    if (sentReceiptInvoiceIdsRef.current.has(receiptKey)) {
      return;
    }

    sentReceiptInvoiceIdsRef.current.add(receiptKey);
    try {
      await dispatch(paymentDownload(receiptPayload)).unwrap();
    } catch (error) {
      console.error("Automatic agreement payment receipt email failed", error);
    }
  };

  const BackToStart = (): void => {
    setIsLoading(true);
    setEditVal("0.00");
    setIsOffer(false);
    setSeconds(0);
    setTimeDifff(null);
    setIsAgreementSent(false);
    setIsAgreementSuccess(false);
    setOfferData({
      offer_description: "hfrh fcnhrf nhfcrvnh vncf hjhj jhhj",
      offer_headline: "Get 10% off",
    });
    setOrderData({
      payment_schedule: "Daily",
      total_payments: "5",
      offer_amount: 550,
      grandTotal: 800,
      creation_date: "20/10/2024",
    });
    setDate(undefined);
    setExpandedDate(undefined);
    setScheduled([]);
  };

  const { allowPendingNavigation, cancelPendingNavigation } =
    useNavigationGuard({
      shouldBlock: () => isPaymentProcessing,
      onNavigationAttempt: () => setShowModal(true),
    });

  const handleConfirmLeave = (): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    dispatch(setIsPaymentProcessing(false));
    setShowModal(false);
    setTimeout(() => allowPendingNavigation(), 250);
  };

  const handleCancelLeave = (): void => {
    setShowModal(false);
    cancelPendingNavigation();
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPaymentProcessing) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isPaymentProcessing]);

  useEffect(() => {
    if (isLogin && orderData && userData) {
      const accounts_id = orderData?.accounts_id;
      const id = userData?.data?.accountData?.id;

      if (id && accounts_id && id !== accounts_id) {
        clearSlice(dispatch);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        dispatch(updateTokenExpiredStatus(false));
        dispatch(updateTokenStatus(false));
        localStorage.clear();
        window.location.reload();
      }
    }
  }, [orderData, isLogin, userData]);

  const handleStore = async ({
    customerData,
    companyLogoDetails,
    paymentPermission,
  }: HandleStoreParams): Promise<void> => {
    dispatch(updatePaymentPermission(paymentPermission));
    setPaymentLogo(companyLogoDetails?.logo);
    dispatch(updateLogoAndFavicon(companyLogoDetails));
    dispatch(
      updateUserData({
        ...userData?.data?.accountData,
        ...customerData,
      })
    );
  };

  const onGetPaymentAgreementData = async (): Promise<void> => {
    try {
      const payload = { slug: actionId };
      const res = (await dispatch(
        getPaymentAgreementData(payload)
      ).unwrap()) as unknown as PaymentAgreementResponse;

      if (isInvalidPublicLinkResult(res)) {
        goToInvalid(navigate, "link");
        return;
      }

      const { data, success } = res?.body;
      if (success) {
        const {
          getAgreementDetailsData,
          paymentPermission,
          customerData,
          companyLogoDetails,
          oldData = null,
        } = data;

        if (
          !isAllowedClusterId(String(getAgreementDetailsData?.clusterid ?? ""))
        ) {
          goToInvalid(navigate, "link");
          return;
        }

        const canonicalAgreementUrl = buildCanonicalAgreementRedirectUrl({
          currentSlug: actionId,
          canonicalSlug: getAgreementDetailsData?.agreementid,
          origin: window.location.origin,
          search: location.search,
          hash: location.hash,
        });

        if (canonicalAgreementUrl) {
          const canonicalAgreementRoute = getSameOriginAgreementRoute(
            canonicalAgreementUrl,
            window.location.origin
          );

          if (!canonicalAgreementRoute) {
            goToInvalid(navigate, "link");
            return;
          }

          // Keep the canonical public URL without reloading the document or
          // requesting agreement data a second time. The response below is
          // already the data for this canonical agreement.
          skipNextAgreementFetchRef.current = true;
          navigate(canonicalAgreementRoute, { replace: true });
        }

        const newData: OrderData = {
          ...getAgreementDetailsData,
          grandTotal: getAgreementDetailsData?.amount
            ? getAgreementDetailsData?.amount
            : 0,
        };

        await handleStore({
          paymentPermission,
          customerData,
          companyLogoDetails,
        });
        // The terms name the company by its legal name, which the agreement
        // response does not carry. Guests never load get-markers, and the
        // company markers kept from an earlier login may be another cluster's.
        dispatch(
          companyMarkersGet({ cluster_id: getAgreementDetailsData?.clusterid })
        );
        setOldAgreement(oldData);

        let date, adjustedDate, timeDiff;

        if (
          getAgreementDetailsData?.expiry_date ||
          getAgreementDetailsData?.expiryDate
        ) {
          date =
            getAgreementDetailsData?.expiry_date ||
            getAgreementDetailsData?.expiryDate ||
            "";
          const currentDate = moment.utc();
          // Same formatter as Action Center's Due Date column, so both pages
          // show the same day for this agreement.
          adjustedDate = formatPaymentListDate(
            date,
            userData?.data?.accountData?.timezone,
            "short"
          );
          timeDiff = Math.abs(currentDate.diff(moment.utc(date), "seconds"));
        } else {
          const obj = getCountDownAgreement(
            getAgreementDetailsData?.pp_oe_time || "",
            getAgreementDetailsData?.next_billing_date || "",
            getAgreementDetailsData?.creation_date || "",
            getAgreementDetailsData?.agreement_tz || ""
          );
          ({ date, adjustedDate, timeDiff } = obj);
        }

        setExpandedDate(date?.toString());
        setDate(adjustedDate);
        setIsAgreementSent(data?.requested || false);
        setIsAgreementSuccess(data?.requested || false);
        setRequestStatus(data?.requestedStatus || "Pending");
        setTimeDifff(!isExpired(date) ? timeDiff : 0);
        setOrderData(newData);
        setIsOffer(getAgreementDetailsData?.settlement_offer === "Y");
        setOfferData({
          offer_description: getAgreementDetailsData?.offer_description || "",
          offer_headline: getAgreementDetailsData?.offer_headline || "",
          offer_amount: getAgreementDetailsData?.offer_amount,
          offer_percentage: getAgreementDetailsData?.offer_percentage,
          offer_type: getAgreementDetailsData?.offer_type,
          settlement_offer: getAgreementDetailsData?.settlement_offer,
        });
        const installmentArr: ScheduledItem[] = [
          ...(getAgreementDetailsData?.clients_schedule_custom_data || []),
        ];
        if (installmentArr?.length > 0) {
          setEditVal(installmentArr[0].amount.toFixed(2));
        }
        setScheduled([...installmentArr]);

        if (
          getAgreementDetailsData?.agreement_status?.toLowerCase() !==
            "completed" &&
          getAgreementDetailsData?.agreement_status?.toLowerCase() !==
            "accepted"
        ) {
          const slug = decode(actionId || "")?.split("_") || [];
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "payment_agreement",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "clicked",
              activity_type: "payment_activity",
              clusterid: slug[1] || "",
              communication_status: "",
              accounts_id: getAgreementDetailsData?.accounts_id ||userData?.data?.accountData?.id,
              payment_amount: getAgreementDetailsData?.amount,
              plan_id: slug[0] || "",
            })
          );
        }
      }
    } catch (error) {
      if (isInvalidPublicLinkResult(error)) {
        goToInvalid(navigate, "link");
        return;
      }
      console.log("onGetPaymentAgreementData error=====", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onResolveLegacyAgreementSlug = async (
    legacySlug: string
  ): Promise<void> => {
    try {
      const response = (await dispatch(
        getLegacyAgreementSlug({ slug: legacySlug })
      ).unwrap()) as LegacyAgreementResolverResponse;
      const canonicalSlug = getCanonicalAgreementSlug(response);
      const canonicalAgreementUrl = buildCanonicalAgreementRedirectUrl({
        currentSlug: legacySlug,
        canonicalSlug: canonicalSlug || undefined,
        origin: window.location.origin,
        search: location.search,
        hash: location.hash,
      });

      if (!canonicalAgreementUrl) {
        goToInvalid(navigate, "link");
        return;
      }

      const canonicalAgreementRoute = getSameOriginAgreementRoute(
        canonicalAgreementUrl,
        window.location.origin
      );

      if (!canonicalAgreementRoute) {
        goToInvalid(navigate, "link");
        return;
      }

      // Legacy links still need one agreement-data request after resolution,
      // but the route transition itself does not need a document reload.
      navigate(canonicalAgreementRoute, { replace: true });
    } catch (error) {
      console.log("onResolveLegacyAgreementSlug error=====", error);
      goToInvalid(navigate, "link");
    }
  };

  const createPayload = async (
    values: SignAgreementValues
  ): Promise<AgreementPayload> => {
    let payload: AgreementPayload;
    const { isAddNewCard, dataPayment } = values?.cardData;
    if (isAddNewCard) {
      let method = "CC";
      if (
        dataPayment?.selectedBank &&
        Object.keys(dataPayment.selectedBank).length > 0
      ) {
        method = "ACH";
      }
      if (
        dataPayment?.selectedWallet &&
        Object.keys(dataPayment.selectedWallet).length > 0
      ) {
        method = "WT";
      }

      let selectedData = dataPayment?.selectedCard;
      if (method === "CC") {
        selectedData = dataPayment?.selectedCard;
      } else if (method === "WT") {
        selectedData = dataPayment?.selectedWallet;
        method = selectedData?.payment_method || "WT";
      } else {
        selectedData = dataPayment?.selectedBank;
      }
      payload = {
        slug: actionId || "",
        plan_id: orderData?.id,
        lead_id: orderData?.lead_id,
        x_method: method || "",
        bcity: selectedData?.billing_city,
        baddress1: selectedData?.billing_address,
        baddress2: selectedData?.billing_address2,
        bzipcode: selectedData?.billing_zip,
        bstate: selectedData?.billing_state,
        bcountry: selectedData?.billing_country,
        x_email: values?.cardData?.savedCardsEmail || "",
        creditcard: selectedData?.cc_4digit,
        choldername: selectedData?.account_name,
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        aholdername: selectedData?.account_name,
        cvv: selectedData?.cc_cvv,
        expiry: selectedData?.cc_expiry,
        card_type: selectedData?.cc_type,
        card_last4: selectedData?.cc_4digit,
        routing_number: selectedData?.routing_number,
        account_number: selectedData?.account_number,
        bank_name: selectedData?.bank_name,
        bank_address: selectedData?.bank_address,
        search_address: "",
        sign_base64: values.sign_base64,
        cc_token: selectedData?.cc_token,
        cc_token2: selectedData?.cc_token2,
        use_old_card: "1",
        cppid: selectedData?.id,
        clusterid: orderData?.clusterid,
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        custom_schedule: 'Y'
      };
    } else {
      payload = {
        slug: actionId || "",
        plan_id: orderData?.id,
        lead_id: orderData?.lead_id,
        x_method: values?.cardData?.x_payment_method || "",
        bcity: values?.cardData.x_city || userData?.data?.accountData?.city,
        baddress1:
          values?.cardData.x_address || userData?.data?.accountData?.address1,
        baddress2:
          values?.cardData.address2 || userData?.data?.accountData?.address2,
        bzipcode:
          values?.cardData.x_zip || userData?.data?.accountData?.postal_code,
        bstate: values?.cardData.x_state || userData?.data?.accountData?.state,
        bcountry:
          values?.cardData.x_country || userData?.data?.accountData?.country,
        x_email: userData?.data?.accountData?.email || "",
        creditcard:
          values?.cardData.x_payment_method === "CC"
            ? values?.cardData.x_card_num?.replaceAll(" ", "")
            : "",
        choldername: values?.cardData.choldername,

        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        aholdername: values?.cardData.choldername,
        cvv:
          values?.cardData.x_payment_method === "CC"
            ? values?.cardData.x_card_code
            : "",
        expiry:
          values?.cardData.x_payment_method === "CC"
            ? values?.cardData.x_exp_date
            : "",
        card_type:
          values?.cardData.x_payment_method === "CC"
            ? values?.cardData.card_type ||
              (values?.cardData.x_card_num
                ? getCardType(values.cardData.x_card_num.replaceAll(" ", ""))
                : "")
            : "",
        card_last4:
          values?.cardData.x_payment_method === "CC"
            ? values?.cardData.x_card_num?.slice(-4)
            : "",
        routing_number:
          values?.cardData.x_payment_method === "ACH"
            ? values?.cardData.routing_number
            : "",
        account_number:
          values?.cardData.x_payment_method === "ACH"
            ? values?.cardData.account_number
            : "",
        bank_name:
          values?.cardData.x_payment_method === "ACH"
            ? values?.cardData.bankName
            : "",
        bank_address:
          values?.cardData.x_payment_method === "ACH"
            ? values?.cardData.bank
            : "",
        cc_token: values?.isCCTokenAvailable ? values?.cardData?.cc_token : "",
        cc_token2: values?.isCCTokenAvailable
          ? values?.cardData?.cc_token2
          : "",
        pc_atid: values?.cardData?.pc_atid,
        cppid: values?.isCCTokenAvailable ? values?.cardData?.cppid : 0,
        search_address: "",
        sign_base64: values.sign_base64,
        use_old_card: values?.isCCTokenAvailable ? "1" : "0",
        clusterid: orderData?.clusterid,
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        custom_schedule: 'Y'
      };
    }

    return payload;
  };
  const paymentAgreementProcess = async (
    values: SignAgreementValues
  ): Promise<any> => {
    dispatch(setIsPaymentProcessing(true));
    try {
      const payload = await createPayload(values);
      const res: any = await dispatch(signAgreement(payload)).unwrap();
      if (res?.body?.success) {
        await sendEmail({
          invoice_id: res?.body?.data?.invoice_id,
          emails: res?.body?.data?.email,
        });
      }
      return res;
    } catch (error) {
      // An uncertain failure (timeout, 5xx) is already resolved by the
      // signAgreement thunk; what reaches here is a definite rejection, which
      // must be shown rather than silently re-enabling Confirm.
      console.log("SignAgreement error =====", error);
      return { body: { success: false, data: { msg: SIGNING_FAILED_MSG } } };
    } finally {
      dispatch(setIsPaymentProcessing(false));
    }
  };

  const createGooglePayPayload = ({
    tokenData,
    sign_base64,
    method = "GP",
    paypal_token,
  }: {
    tokenData?: GooglePayData;
    sign_base64?: string;
    method?: string;
    paypal_token?: string;
  }): AgreementPayload => {
    let payload: AgreementPayload;
    if (method === "GP" && tokenData && "tokenizationData" in tokenData) {
      const { tokenizationData } = tokenData;
      const resp = JSON.parse(tokenizationData.token);
      payload = {
        slug: actionId || "",
        plan_id: orderData?.id,
        lead_id: orderData?.lead_id,
        stripe_token: resp?.id,
        x_method: "GP",
        bcity: resp?.card?.address_city || "",
        baddress1: resp?.card?.address_line1 || "",
        baddress2: resp?.card?.address_line2 || "",
        bzipcode: resp?.card?.address_zip || "",
        bstate: resp?.card?.address_state || "",
        bcountry: resp?.card?.address_country || "",
        x_email: userData?.data?.accountData?.email || "",
        creditcard: resp?.card?.last4 || "",
        choldername: resp?.card?.name || "",
        aholdername: "",
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        cvv: "***",
        expiry: resp?.card?.exp_month + "/" + resp?.card?.exp_year || "",
        card_type: resp?.card?.brand || "",
        card_last4: resp?.card?.last4 || "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        search_address: "",
        sign_base64: sign_base64 || "",
        clusterid: orderData?.clusterid,
        use_old_card: "0",
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        custom_schedule: 'Y'
      };
    } else if (method === "GP" && tokenData && "id" in tokenData) {
      payload = {
        slug: actionId || "",
        plan_id: orderData?.id,
        lead_id: orderData?.lead_id,
        x_method: "GP",
        transaction_id: tokenData.id,
        is3ds: true,
        x_email: userData?.data?.accountData?.email || "",
        creditcard: "",
        choldername: "",
        aholdername: "",
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        cvv: "***",
        expiry: "",
        card_type: "",
        card_last4: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        search_address: "",
        sign_base64: sign_base64 || "",
        clusterid: orderData?.clusterid,
        use_old_card: "0",
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        custom_schedule: 'Y'
      };
    } else if (method === "PP") {
      payload = {
        slug: actionId || "",
        plan_id: orderData?.id || "",
        lead_id: orderData?.lead_id,
        paypal_token,
        x_method: "PP",
        x_email: userData?.data?.accountData?.email || "",
        creditcard: "",
        choldername: "",
        aholdername: "",
        desc: "Online Payment",
        description: "Online Payment",
        payment_note: "Payment Agreement",
        cvv: "***",
        expiry: "",
        card_type: "",
        card_last4: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        search_address: "",
        sign_base64: signatureForAgreement(payPalOrder, actionId),
        clusterid: orderData?.clusterid || "",
        use_old_card: "0",
        timezone: getBrowserTimezoneOffset(),
        customer_timezone: getBrowserTimezoneName(),
        custom_schedule: 'Y'
      };
    } else {
      throw new Error("Invalid wallet payment payload.");
    }
    return payload;
  };

  const handleGooglePay = async (
    method: string = "GP",
    tokenData?: GooglePayData,
    sign_base64?: string,
    paypal_token: string = ""
  ): Promise<any> => {
    dispatch(setIsPaymentProcessing(true));
    setIsGooglePayLoading(true);
    try {
      const payload = await createGooglePayPayload({
        tokenData,
        sign_base64,
        method,
        paypal_token,
      });
      const res: any = await dispatch(signAgreement(payload)).unwrap();
      if (res?.body?.success) {
        await sendEmail({
          invoice_id: res?.body?.data?.invoice_id,
          emails: res?.body?.data?.email,
        });
      }
      return res;
    } catch (error) {
      console.log("handleGooglePay error =====", error);
    } finally {
      setIsGooglePayLoading(false);
      dispatch(setIsPaymentProcessing(false));
    }
  };

  useEffect(() => {
    if (skipNextAgreementFetchRef.current) {
      skipNextAgreementFetchRef.current = false;
      return;
    }

    if (!actionId) {
      goToInvalid(navigate, "link");
      return;
    }

    if (actionId.startsWith("agr_")) {
      // Canonical agreement slugs are opaque backend-owned identifiers. They
      // do not necessarily decode to the frontend's record/cluster/timestamp
      // format used by Action Center fallback links.
      if (!isCanonicalAgreementSlug(actionId)) {
        goToInvalid(navigate, "link");
        return;
      }

      onGetPaymentAgreementData();
      return;
    }

    if (!isLegacyAgreementSlug(actionId)) {
      goToInvalid(navigate, "link");
      return;
    }

    onResolveLegacyAgreementSlug(actionId);
  }, [actionId]);

  useEffect(() => {
    if (oldAgreement) {
      if (oldAgreement.status === "Replaced" && oldAgreement.replaced_by) {
        skipNextAgreementFetchRef.current = true;
        setIsReplacedPopup(true);
        navigate(`/agreement/${oldAgreement.replaced_by}`);
      }
    }
  }, [oldAgreement]);

  useEffect(() => {
    if (timeDifff) {
      setSeconds(timeDifff);

      const interval = setInterval(() => {
        setSeconds((prevSeconds: any) => {
          if (prevSeconds <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timeDifff]);

  useEffect(() => {
    if (searchParams.has("method")) {
      setInitialDisclosureAccept(true);
    }
  }, []);

  // The captured signature is persisted to the browser, so it has to be dropped
  // when this page goes away. Navigating to another page and coming back — or
  // opening a different agreement — would otherwise start out already signed
  // with the signature drawn here.
  // actionId is a dependency because routing straight from one agreement to
  // another keeps this component mounted, so unmount alone would not fire.
  useEffect(() => {
    return () => {
      dispatch(clearAgreementSignature());
    };
  }, [dispatch, actionId]);

  //drawer
  useEffect(() => {
    const updateMaxHeight = () => {
      if (drawerRef.current && !isLoading) {
        drawerRef.current.style.maxHeight = showDetails
          ? `${drawerRef.current.scrollHeight}px`
          : "0";
      }
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [showDetails, isLoading]);

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: editVal,
    nextDate: moment(orderData?.billing_start_date).format("MM/DD/YYYY"),
    paymentAgreement: window.location.href,
  });

  const handleStatusOnAccept = () => {
    if (orderData?.agreement_status?.toLowerCase() === "pending") {
      setOrderData((state) => ({ ...state, agreement_status: "Accepted" }));
    }
  };

  const scrollDocumentToTop = useCallback((): void => {
    const scrollToTop = () => {
      documentContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo({ top: 0, behavior: "auto" });
      window.scrollTo({ top: 0, behavior: "auto" });
    };

    scrollToTop();
    requestAnimationFrame(scrollToTop);
    window.setTimeout(scrollToTop, 0);
  }, []);

  return (
    <div
      className={`w-full lg:h-[var(--shell-vh)] lg:grid grid-cols-2 lg:overflow-hidden`}
      {...testAttrs("page", "Payment agreement")}
    >
      {/* PAYMENT DESCRIPTION PART */}
      <div
        style={{ backgroundColor: brandColor }}
        className="relative lg:h-[var(--shell-vh)] "
        {...testAttrs("details-panel", "Payment agreement details")}
      >
        {/* for desktop and tablet view */}
        {orderData?.agreement_status?.toLowerCase() !== "cancelled" &&
          orderData?.agreement_status?.toLowerCase() !== "canceled" &&
          orderData?.agreement_status?.toLowerCase() !== "accepted" &&
          orderData?.agreement_status?.toLowerCase() !== "completed" && (
            <div
              className={`lg:absolute hidden lg:top-0 
              w-full lg:flex justify-between items-center z-10 ${
                isExpired(expandedDate) ? "bg-lightRed2" : "bg-white"
              } lg:px-6 py-[6px] border-r-[2px] border-bgSecondary px-5`}
              {...testAttrs("desktop-expiry-banner", "Agreement expiry")}
            >
              <p className="lg:text-base text-sm font-medium text-txtPrimary w-[40%] break-words">
                Offer {isExpired(expandedDate) ? "expired" : "expires"} on{" "}
                {isLoading ? (
                  <Skeleton width={100} />
                ) : (
                  <span
                    className={`font-bold inline-block whitespace-nowrap ${
                      isExpired(expandedDate)
                        ? "text-red"
                        : "text-btnPrimaryActive"
                    } `}
                  >
                    {date}
                  </span>
                )}
              </p>
              <Countdown
                isLoading={false}
                seconds={!isExpired(expandedDate) ? seconds : 0}
                isExpired={isExpired(expandedDate)}
              />
            </div>
          )}

        {/* main content */}
        <div
          ref={documentContainerRef}
          className={`classScroll w-full ${
            orderData?.agreement_status?.toLowerCase() !== "cancelled" &&
            orderData?.agreement_status?.toLowerCase() !== "canceled" &&
            orderData?.agreement_status?.toLowerCase() !== "accepted" &&
            orderData?.agreement_status?.toLowerCase() !== "completed"
              ? "lg:mt-[110px] md:mt-[45px] mt-[40px]"
              : "lg:mt-[50px] md:mt-[45px] mt-[40px]"
          } lg:h-full lg:overflow-y-scroll lg:max-h-[calc(var(--shell-vh)-120px)] py-5`}
          {...testAttrs("details-scroll", "Agreement details scroll area")}
        >
          <div className="flex flex-col lg:flex-row justify-center lg:mt-[7%] h-fit">
            {/* for mobile and tablet */}
            {orderData?.agreement_status?.toLowerCase() !== "cancelled" &&
              orderData?.agreement_status?.toLowerCase() !== "canceled" &&
              orderData?.agreement_status?.toLowerCase() !== "accepted" &&
              orderData?.agreement_status?.toLowerCase() !== "completed" && (
                <div
                  className={`lg:absolute lg:hidden lg:top-0 
              w-full flex justify-between items-center z-5 ${
                isExpired(expandedDate) ? "bg-lightRed2" : "bg-white"
              } lg:px-6 py-[6px] border-r-[2px] border-bgSecondary px-5`}
                  {...testAttrs("mobile-expiry-banner", "Agreement expiry")}
                >
                  <p className="lg:text-base text-sm font-medium text-txtPrimary w-[40%] break-words">
                    Offer {isExpired(expandedDate) ? "expired" : "expires"} on{" "}
                    {isLoading ? (
                      <Skeleton width={100} />
                    ) : (
                      <span
                        className={`font-bold inline-block whitespace-nowrap ${
                          isExpired(expandedDate)
                            ? "text-red"
                            : "text-btnPrimaryActive"
                        } `}
                      >
                        {date}
                      </span>
                    )}
                  </p>
                  <Countdown
                    isLoading={false}
                    seconds={!isExpired(expandedDate) ? seconds : 0}
                    isExpired={isExpired(expandedDate)}
                  />
                </div>
              )}

            <div
              className={`flex flex-col items-start lg:gap-10 gap-4 w-full lg:max-w-[380px] lg:mx-5 lg:px-0 px-5 lg:pt-0 pt-5`}
              {...testAttrs("summary", "Agreement summary")}
            >
              {/* company logo */}
              <div className="lg:flex hidden items-center gap-[10px]">
                {isLoading ? (
                  <Skeleton width={100} height={35} />
                ) : (
                  <div className="h-16 lg:h-16 flex justify-end">
                    <img
                      src={paymentLogo}
                      alt="Company logo"
                      className="animate__animated animate__fadeIn object-contain"
                    />
                  </div>
                )}
              </div>

              {/* payment type and amount */}
              <div className={`flex flex-col items-start w-full`}>
                <span className="w-full flex justify-between items-center">
                  <div
                    className={`${
                      isDark ? "text-darktxtParagraph" : "text-txtParagraph"
                    } font-bold text-xl leading-[26.04px]`}
                  >
                    Payment Agreement
                  </div>
                  <div
                    className={`lg:hidden text-base font-medium underline ${
                      isDark
                        ? "text-darkTxtBlue hover:!text-darkBlueHover"
                        : "text-blue hover:!text-btnPrimaryHover"
                    } cursor-pointer`}
                    role="button"
                    tabIndex={0}
                    {...testAttrs(
                      "toggle-details",
                      showDetails
                        ? "Hide agreement details"
                        : "View agreement details"
                    )}
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails ? "Hide details" : "View details"}
                  </div>
                </span>

                <div className="flex justify-between w-full mt-4 relative">
                  <div
                    className={`font-bold lg:leading-[65.1px] leading-[41.66px] ${
                      isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                    } lg:text-[44px] text-[32px]`}
                  >
                    {formatCurrency(editVal)}
                  </div>

                  <div className={`absolute right-0 lg:-top-[26px] -top-2  `}>
                    {!isLoading && (
                      <img
                        src={orderData?.qrcode || ScannerImg}
                        alt="Payment agreement QR code"
                        className={`lg:h-[90px] lg:w-[90px] h-[60px] w-[60px]  ${
                          isDark ? "invert" : "mix-blend-multiply"
                        }`}
                      />
                    )}
                  </div>
                </div>

                <div
                  className={`text-sm ${
                    isDark ? "text-darkTxtSecondary" : "text-txtSecondary"
                  }`}
                >
                  1st Installment
                </div>
              </div>

              {/* details */}
              <span
                ref={drawerRef}
                className={`drawer1 ${
                  showDetails && "open"
                } flex flex-col w-full gap-5 lg:max-w-[380px]`}
                {...testAttrs("account-details", "Agreement account details")}
              >
                {/* LINE */}
                <div className="h-[1px] bg-strokeActive w-full" />
                {/* ACCOUNT DETAILS */}
                <div className="flex flex-col gap-4 w-full">
                  <div className="flex flex-col gap-3 w-full">
                    <div className="flex justify-between items-center">
                      <div
                        className={`leading-[26.04px] ${
                          isDark ? "text-darkTxtBlue" : "text-btnPrimaryActive"
                        } font-medium text-xl`}
                      >
                        {isLoading ? (
                          <Skeleton width={150} />
                        ) : (
                          (userData?.data?.accountData?.first_name || "") +
                          " " +
                          (userData?.data?.accountData?.last_name || "")
                        )}
                      </div>
                      <div
                        className={`text-sm ${
                          isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                        }`}
                      >
                        {isLoading ? (
                          <Skeleton width={80} />
                        ) : (
                          userData?.data?.accountData?.accountid
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <div className="grid grid-cols-2 w-full">
                        <div
                          className={`leading-[20px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-sm text-left`}
                        >
                          Balance
                        </div>
                        <div
                          className={`leading-[20px] ${
                            isDark
                              ? "text-darkTxtSecondary"
                              : "text-txtSecondary"
                          } text-sm text-right`}
                        >
                          Description
                        </div>
                      </div>
                      <div className="grid grid-cols-2 w-full">
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-left`}
                        >
                          {isLoading ? (
                            <Skeleton width={70} />
                          ) : (
                            <>
                              {formatCurrency(
                                userData?.data?.accountData?.balance
                              ) || "$0.00"}
                            </>
                          )}
                        </div>
                        <div
                          className={`leading-[20px] ${
                            isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                          } text-sm text-right`}
                        >
                          {isLoading ? (
                            <Skeleton width={80} />
                          ) : (
                            (userData?.data?.accountData?.description ?? "--")
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* LINE */}
                  <div className="h-[1px] bg-strokeActive w-full" />

                  {/* agreemnet details */}
                  <div className="py-6 w-full">
                    <div className="py-5 bg-bgPrimary border-x border-t border-strokePrimary rounded-t-lg">
                      <div className="flex flex-col items-start gap-1">
                        <div className="px-6 text-sm leading-normal font-bold uppercase">
                          {isOffer ? "OFFER TERMS" : "TERMS"}
                        </div>
                        {isOffer ? (
                          ""
                        ) : (
                          <>
                            <div className="px-6 mt-2 h-[1px] w-full bg-strokePrimary" />
                          </>
                        )}
                        <div className="flex flex-col items-start w-full">
                          {isOffer ? (
                            <>
                              <div className="flex bg-bgThird w-full gap-4 px-6 py-5 mt-[12px]">
                                <div>
                                  <OfferIcon aria-hidden="true" />
                                </div>
                                <div>
                                  <p
                                    className={`text-xl font-bold text-txtPrimary`}
                                  >
                                    {OfferData?.offer_headline}
                                  </p>
                                  <p className="text-sm font-normal text-txtParagraph">
                                    {OfferData?.offer_description}
                                  </p>
                                </div>
                              </div>
                            </>
                          ) : (
                            ""
                          )}
                        </div>
                        <div className="px-6 flex flex-col items-start gap-4 w-full ">
                          <div className=" flex items-center justify-between w-full mt-3">
                            <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph ">
                              <p>Start date </p>
                              <div className="grid place-items-center">
                                <CustomTooltip
                                  maxwidth="280px"
                                  place="top"
                                  id="start-date-anchor"
                                  content={`<div class="text-xs leading-[18px] font-normal">The date when the first payment installment is scheduled to be processed.</div>`}
                                >
                                  <InfoIcon
                                    id="start-date-anchor"
                                    aria-hidden="true"
                                    className="cursor-pointer group"
                                    _className="group-hover:!stroke-iconObscure"
                                  />
                                </CustomTooltip>
                              </div>
                            </div>
                            <div className="font-medium text-sm leading-normal text-txtPrimary text-right">
                              {isLoading ? (
                                <Skeleton width={150} />
                              ) : (
                                formatPaymentListDate(
                                  orderData?.billing_start_date || "",
                                  orderData?.agreement_tz || "",
                                  "long"
                                )
                              )}
                            </div>
                          </div>
                          <div className="h-[1px] w-full bg-strokePrimary" />
                          <div className="flex items-center justify-between w-full">
                            <div className="font-normal flex items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                              <p>Frequency</p>{" "}
                              <div className="grid place-items-center">
                                <CustomTooltip
                                  maxwidth="280px"
                                  place="top"
                                  id="frequency-anchor"
                                  content={`<div class="text-xs leading-[18px] font-normal">Defines how often the installments will be processed.</div>`}
                                >
                                  <div
                                    id="frequency-anchor"
                                    className="cursor-pointer pointer-events-auto"
                                  >
                                    <InfoIcon aria-hidden="true" />
                                  </div>
                                </CustomTooltip>
                              </div>
                            </div>
                            <div className="font-normal text-sm leading-normal text-txtPrimary">
                              {isLoading ? (
                                <Skeleton width={80} />
                              ) : (
                                <>
                                  {changePaymentScheduleName(
                                    orderData?.payment_schedule
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="h-[1px] w-full bg-strokePrimary" />

                          <div className="flex items-center justify-between w-full">
                            <div className="flex font-normal items-center gap-1 cursor-pointer text-sm leading-normal text-txtParagraph">
                              <p>Number of transactions</p>
                              <div className="grid place-items-center">
                                <CustomTooltip
                                  maxwidth="280px"
                                  place="top"
                                  id="transaction-anchor"
                                  content={`<div class="text-xs leading-[18px] font-normal">The total number of payment installments within this schedule.</div>`}
                                >
                                  <div
                                    id="transaction-anchor"
                                    className="cursor-pointer pointer-events-auto"
                                  >
                                    <InfoIcon aria-hidden="true" />
                                  </div>
                                </CustomTooltip>
                              </div>
                            </div>
                            <div className="font-normal text-sm leading-normal text-txtPrimary">
                              {isLoading ? (
                                <Skeleton width={80} />
                              ) : (
                                orderData.total_payments
                              )}
                            </div>
                          </div>

                          {isOffer && (
                            <>
                              <div className="h-[1px] w-full bg-strokePrimary" />
                              <div className="flex items-center justify-between w-full">
                                <div className="flex font-bold items-center gap-1 text-sm leading-normal text-txtPrimary">
                                  <p>Discount amount</p>
                                  <div className="grid place-items-center">
                                    <CustomTooltip
                                      maxwidth="280px"
                                      place="top"
                                      id="discount-payments-anchor"
                                      content={`<div class="text-xs leading-[18px] font-normal">Total discounted amount for this plan.</div>`}
                                    >
                                      <div
                                        id="discount-payments-anchor"
                                        className="cursor-pointer pointer-events-auto"
                                      >
                                        <InfoIcon aria-hidden="true" />
                                      </div>
                                    </CustomTooltip>
                                  </div>
                                </div>
                                <div className="font-bold text-sm text-txtPrimary">
                                  {isLoading ? (
                                    <Skeleton width={80} />
                                  ) : (
                                    formatCurrency(
                                      OfferData?.offer_type === "P" &&
                                        (OfferData?.offer_percentage || 0) > 0
                                        ? ((OfferData?.offer_percentage || 0) *
                                            (orderData?.initial_balance || 0)) /
                                            100
                                        : OfferData?.offer_amount
                                    ) || "$0.00"
                                  )}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="px-6 py-4 bg-bgThird border-x border-b border-strokePrimary rounded-b-lg border-t ">
                      <div className="flex items-center justify-between w-full">
                        <div className="text-xl leading-normal flex gap-1 items-center text-txtPrimary">
                          <p className="font-bold uppercase">Total</p>
                          <CustomTooltip
                            maxwidth="280px"
                            place="top"
                            id="total-payments-anchor"
                            content={`<div class="text-xs leading-[18px] font-normal">Total plan amount with <span class="lowercase">
                  ${
                    portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"
                  }
                </span> included.</div>`}
                          >
                            <div
                              id="total-payments-anchor"
                              className="cursor-pointer pointer-events-auto"
                            >
                              <InfoIcon aria-hidden="true" />
                            </div>
                          </CustomTooltip>
                        </div>

                        <div className="text-xl flex items-center gap-2 text-txtPrimary font-bold">
                          {isLoading ? (
                            <Skeleton width={100} />
                          ) : isOffer ? (
                            formatCurrency(orderData?.totalAmount) || "$0.00"
                          ) : (
                            formatCurrency(orderData?.totalAmount) || "0.00"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </span>
              <Footer
                style={{ backgroundColor: brandColor }}
                className={`!justify-start xl:!w-full !w-[42.5%] lg:max-w-[380px] lg:flex hidden xl:flex-nowrap flex-wrap ${
                  isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
      {/* PAYMENT METHOD SECTION */}
      <div
        className="w-full lg:h-[var(--shell-vh)] lg:overflow-y-auto"
        {...testAttrs("payment-section", "Agreement payment section")}
      >
        {!isLoading && (
          <>
            {["opened", "expired", "pending"].includes(
              orderData?.agreement_status?.toLowerCase() || ""
            ) ? (
              isExpired(expandedDate) ||
              orderData?.agreement_status?.toLowerCase() === "expired" ? (
                !isAgreementSent ? (
                  <PaymentAgreementExpired
                    agreement_id={orderData?.agreementid}
                    setIsAgreementSent={setIsAgreementSent}
                    setIsAgreementSuccess={setIsAgreementSuccess}
                    setAgreementFailedReason={setAgreementSentFailed}
                    startDate={orderData?.billing_start_date || ""}
                    Cancelled={
                      orderData?.agreement_status?.toLowerCase() ===
                        "cancelled" ||
                      orderData?.agreement_status?.toLowerCase() === "canceled"
                    }
                  />
                ) : (
                  <PaymentAgreementSuccess
                    success={isAgreementSuccess}
                    requestStatus={requestStatus}
                    failedReason={agreementSentFailed}
                  />
                )
              ) : portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
                initialDisclosureAccept ? (
                <ActionPaymentMethod
                  key={actionId}
                  scheduled={scheduled}
                  orderData={orderData}
                  paymentAgreementProcess={paymentAgreementProcess}
                  BackToStart={BackToStart}
                  handleGooglePay={handleGooglePay}
                  isGooglePayLoading={isGooglePayLoading}
                  isSigned={
                    orderData?.agreement_status?.toLowerCase() === "accepted"
                  }
                  setInitialDisclosureAccept={setInitialDisclosureAccept}
                  generalMarkers={generalMarkers}
                  handleStatusOnAccept={handleStatusOnAccept}
                  scrollDocumentToTop={scrollDocumentToTop}
                  action="agreement"
                />
              ) : (
                <div
                  className="h-full lg:max-h-[var(--shell-vh)] relative content w-full lg:pt-20 lg:pb-[40px] xl:px-20 lg:px-10 md:p-10 p-5"
                  {...testAttrs(
                    "initial-disclosure",
                    "Initial agreement disclosure"
                  )}
                >
                  <DisclosureInitial
                    isOpen={true}
                    handleAccept={() => {
                      setInitialDisclosureAccept(true);
                      scrollDocumentToTop();
                      window.setTimeout(scrollDocumentToTop, 100);
                    }}
                    generalMarkers={generalMarkers}
                  />
                </div>
              )
            ) : ["accepted", "completed"].includes(
                orderData?.agreement_status?.toLowerCase() || ""
              ) ? (
              <ActionPaymentMethod
                key={actionId}
                scheduled={scheduled}
                orderData={orderData}
                paymentAgreementProcess={paymentAgreementProcess}
                BackToStart={BackToStart}
                handleGooglePay={handleGooglePay}
                isGooglePayLoading={isGooglePayLoading}
                isSigned={
                  orderData?.agreement_status?.toLowerCase() === "accepted"
                }
                setInitialDisclosureAccept={setInitialDisclosureAccept}
                generalMarkers={generalMarkers}
                handleStatusOnAccept={handleStatusOnAccept}
                scrollDocumentToTop={scrollDocumentToTop}
              />
            ) : ["cancelled", "canceled"].includes(
                orderData?.agreement_status?.toLowerCase() || ""
              ) ? (
              !isAgreementSent ? (
                <PaymentAgreementExpired
                  agreement_id={orderData?.agreementid}
                  setIsAgreementSent={setIsAgreementSent}
                  setIsAgreementSuccess={setIsAgreementSuccess}
                  setAgreementFailedReason={setAgreementSentFailed}
                  startDate={orderData?.billing_start_date || ""}
                  Cancelled={
                    orderData?.agreement_status?.toLowerCase() ===
                      "cancelled" ||
                    orderData?.agreement_status?.toLowerCase() === "canceled"
                  }
                />
              ) : (
                <PaymentAgreementSuccess
                  success={isAgreementSuccess}
                  requestStatus={requestStatus}
                  failedReason={agreementSentFailed}
                />
              )
            ) : null}
          </>
        )}
      </div>
      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={showModal}
        title="Alert"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        doneText="Leave"
        cancleText="Stay"
        onDoneClick={handleConfirmLeave}
        onCloseClick={handleCancelLeave}
        children={
          <>
            <div
              className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]"
              {...testAttrs("leave-page-alert")}
            >
              Are you sure you want to leave the page? All progress will be
              lost.
            </div>
          </>
        }
      />
      <CustomModal
        signModal={true}
        isLoading={false}
        isOpen={isReplacedPopup}
        title="Alert"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        doneText="Ok"
        cancleText=""
        onDoneClick={() => setIsReplacedPopup(false)}
        onCloseClick={() => setIsReplacedPopup(false)}
        children={
          <div
            className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]"
            {...testAttrs("replaced-agreement-alert")}
          >
            The payment agreement has been replaced. Please review the updated
            terms before proceeding with the payment.
            <div className="pt-9 w-full flex items-center justify-end">
              <Button
                text="Ok"
                {...testAttrs(
                  "close-replaced-agreement-alert",
                  "Close replaced agreement alert"
                )}
                onClick={() => setIsReplacedPopup(false)}
                className="w-fit"
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

const Agreement = () => (
  <TestIdScope scope="agreement">
    <AgreementContent />
  </TestIdScope>
);

export default Agreement;
